import { useState } from "react";
import { Alert, Button, Divider, InputNumber, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";
import { CloudDownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import ArtifactUploadButton from "./ArtifactUploadButton";
import ActiveVersionSelector from "./ActiveVersionSelector";
import JdkFetchModal from "./JdkFetchModal";
import { KIND_JDK, findArtifact, formatBytes, versionsOf } from "./artifact-utils";

/**
 * JDK paketleri. İstemci GetJDK/{os} ile yalnızca aktif sürümü indirir; sürümler diskte yan yana durur,
 * bu yüzden ileri ve geri geçiş yapılabilir.
 */
export default function JdkTab({ settings, reload }) {
    const { t } = useTranslation();
    const active = settings.launcher?.jdkVersion ?? 0;
    const platforms = settings.platforms ?? [];
    const versions = versionsOf(settings.artifacts, KIND_JDK);

    const [uploadVersion, setUploadVersion] = useState(Math.max(active, ...versions, 0) + 1);
    const [fetchOpen, setFetchOpen] = useState(false);

    const activate = async (versionNumber) => {
        const response = await ApiService.ViewerSettings.SetJdkVersion(versionNumber);
        if (response?.success) reload();
    };

    const remove = async (versionNumber, platform) => {
        const response = await ApiService.ViewerSettings.DeleteArtifact(KIND_JDK, versionNumber, platform);
        if (response?.success) reload();
    };

    const columns = [
        {
            title: t('Version'),
            dataIndex: 'version',
            render: (version) => (
                <Space>
                    <strong>{version}</strong>
                    {version === active && <Tag color="green">{t('Active')}</Tag>}
                </Space>
            )
        },
        ...platforms.map(platform => ({
            title: platform.displayName,
            key: platform.key,
            render: (_, row) => {
                const artifact = findArtifact(settings.artifacts, KIND_JDK, row.version, platform.key);
                if (!artifact) {
                    return <Typography.Text type="secondary">—</Typography.Text>;
                }
                return (
                    <Space direction="vertical" size={0}>
                        <Tooltip title={artifact.originalName}>
                            <span>{formatBytes(artifact.size)}</span>
                        </Tooltip>
                        {artifact.source && <Tag>{t(artifact.source)}</Tag>}
                        <Popconfirm title={t('Delete this package?')} onConfirm={() => remove(row.version, platform.key)}>
                            <Button size="small" type="link" danger disabled={row.version === active}>{t('Delete')}</Button>
                        </Popconfirm>
                    </Space>
                );
            }
        }))
    ];

    const rows = versions.map(version => ({ key: version, version }));

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message={t('Every download is verified against its SHA-256 before it replaces the file on disk. A version can only be activated once its Windows x64 package exists.')}
            />

            <ActiveVersionSelector versions={versions} active={active} onActivate={activate} />

            <Divider style={{ margin: '8px 0' }} />

            <Space align="end" wrap>
                <Button type="primary" icon={<CloudDownloadOutlined />} onClick={() => setFetchOpen(true)}>
                    {t('Download JDK from the internet')}
                </Button>
                <div>
                    <Typography.Text type="secondary">{t('New version number')}</Typography.Text>
                    <InputNumber min={8} max={99} value={uploadVersion} onChange={setUploadVersion} style={{ display: 'block', width: 160 }} />
                </div>
                {platforms.map(platform => (
                    <ArtifactUploadButton
                        key={platform.key}
                        size="small"
                        label={platform.displayName}
                        upload={(file, onProgress) =>
                            ApiService.ViewerSettings.UploadJdk(uploadVersion, platform.key, file, onProgress)}
                        onUploaded={reload}
                    />
                ))}
            </Space>

            <Table rowKey="key" size="small" pagination={false} dataSource={rows} columns={columns} />

            {fetchOpen && (
                <JdkFetchModal
                    open={fetchOpen}
                    platforms={platforms}
                    defaultVersion={uploadVersion}
                    onClose={() => setFetchOpen(false)}
                    onFinished={reload}
                />
            )}
        </Space>
    );
}
